import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, XCircle, AlertTriangle, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

// =====================================================================
// MarkLostDialog — capture the structured Lost reason + optional notes
// =====================================================================
// Calls public.mark_plan_lost(plan_id, reason, notes). On success the
// caller's onMarked() handler refetches the drawer and the kanban.
// =====================================================================

const REASONS = [
  { key: "price",          label: "Price was too high" },
  { key: "time",           label: "Couldn’t commit the time" },
  { key: "went_elsewhere", label: "Went to another clinic" },
  { key: "changed_mind",   label: "Patient changed their mind" },
  { key: "other",          label: "Other (use notes)" },
];

export default function MarkLostDialog({
  open, onOpenChange, planId, planCode, patientName, onMarked,
}) {
  const [reason, setReason]       = useState("price");
  const [notes, setNotes]         = useState("");
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setReason("price");
      setNotes("");
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const submit = async () => {
    if (!planId) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.rpc("mark_plan_lost", {
      p_plan_id: planId,
      p_reason:  reason,
      p_notes:   notes.trim() || null,
    });
    setBusy(false);
    if (err) {
      setError(err);
      toast.error(err.message ?? "Could not mark plan lost.");
      return;
    }
    toast.success("Plan marked Lost.");
    onMarked?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[16px]">
            <XCircle className="w-4 h-4 text-rose-600" strokeWidth={2.25} />
            Mark plan as Lost
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            {planCode ? <span className="font-mono">{planCode}</span> : "This plan"}
            {patientName ? ` · ${patientName}` : ""}
            {" — capture why so the clinic can spot patterns."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div>
            <label className="text-[12px] font-medium text-slate-700 mb-1 block">
              Reason
            </label>
            <div className="space-y-1">
              {REASONS.map(({ key, label }) => (
                <label
                  key={key}
                  className={[
                    "flex items-center gap-2 px-2.5 py-1.5 rounded-md border cursor-pointer text-[13px]",
                    reason === key
                      ? "border-rose-500 bg-rose-50 text-rose-900"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300",
                  ].join(" ")}
                >
                  <input
                    type="radio"
                    name="lost_reason"
                    value={key}
                    checked={reason === key}
                    onChange={(e) => setReason(e.target.value)}
                    className="text-rose-600 focus:ring-rose-500"
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[12px] font-medium text-slate-700 mb-1 block">
              Notes <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Anything specific worth remembering?"
              className="w-full px-2.5 py-1.5 text-[13px] rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-rose-500 focus:border-rose-500 resize-none"
            />
            <div className="mt-0.5 text-[10px] text-slate-400 text-right">
              {notes.length}/500
            </div>
          </div>

          {error ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={2.25} />
              <div>{String(error.message || error)}</div>
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            className="px-3 py-1.5 rounded-md text-[13px] text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="px-3 py-1.5 rounded-md text-[13px] font-medium bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2.25} /> : <XCircle className="w-3.5 h-3.5" strokeWidth={2.25} />}
            Mark Lost
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
